// Billing / access utilities.
//
// The product has exactly TWO states a paying customer can be in — a free trial and a paid
// subscription — plus a LOCKED state for accounts that have not paid. There are NO feature
// tiers: trial and paid get identical functionality.
//
// The previous free/pro/pro+/enterprise ladder was removed rather than left as decoration:
// its per-tier `features` arrays and `limits` were never enforced anywhere (hasFeature,
// isWithinLimit and <UpgradeCTA> all had zero callers), so it described a product that did
// not exist. Access is decided here and nowhere else.
//
// Canonical input is companies.billing_status (NOT NULL, DEFAULT 'trial') — the same column
// the live marketplace RPC match_contractors_to_request gates on — plus trial_end and
// paid_through. Written by the stripe-webhook subscription handler.

export const TIERS = {
  FREE_TRIAL: 'free_trial',
  PAID: 'paid',
  LOCKED: 'locked'
};

// How long a past_due account keeps working while Stripe retries the card. A dunning window
// is the industry norm (Jobber/HCP both dun before restricting) — a declined card is usually
// a bank problem, not a refusal to pay, and an instant cut-off just creates churn + support.
export const GRACE_PERIOD_DAYS = 10;

// Billing states that mean "not paying" and lock immediately. These are the Stripe statuses
// mapped by supabase/functions/stripe-webhook (customer.subscription.* handler).
const LOCKED_STATUSES = [
  'canceled', 'cancelled', 'unpaid', 'incomplete', 'incomplete_expired',
  'suspended', 'inactive', 'paused'
];

const DAY_MS = 24 * 60 * 60 * 1000;

const daysFromNow = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - Date.now()) / DAY_MS);
};

export const TIER_CONFIGS = {
  [TIERS.FREE_TRIAL]: { name: 'Free Trial', badge_color: 'bg-purple-100 text-purple-800' },
  [TIERS.PAID]: { name: 'Active', badge_color: 'bg-green-100 text-green-800' },
  [TIERS.LOCKED]: { name: 'Payment Due', badge_color: 'bg-red-100 text-red-800' }
};

export const getTierConfig = (tier) => TIER_CONFIGS[tier] || TIER_CONFIGS[TIERS.FREE_TRIAL];

/**
 * The ONE place access is decided.
 *
 * @param {{billing_status?: string, trial_end?: string, paid_through?: string}|null} billing
 * @returns {{tier: string, state: 'trial'|'paid'|'grace'|'locked', daysRemaining: number|null, reason: string|null}}
 *   state 'grace' still has full access — it is a warning state, not a lock.
 */
export const getBillingState = (billing) => {
  if (!billing) {
    return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: null, reason: null };
  }

  const status = String(billing.billing_status || '').trim().toLowerCase();

  if (status === 'active' || status === 'paid') {
    return {
      tier: TIERS.PAID,
      state: 'paid',
      daysRemaining: daysFromNow(billing.paid_through),
      reason: null
    };
  }

  if (LOCKED_STATUSES.includes(status)) {
    return { tier: TIERS.LOCKED, state: 'locked', daysRemaining: 0, reason: status };
  }

  if (status === 'past_due') {
    // Grace runs from the end of the period they already paid for.
    const graceLeft = billing.paid_through
      ? daysFromNow(new Date(new Date(billing.paid_through).getTime() + GRACE_PERIOD_DAYS * DAY_MS))
      : null;
    if (graceLeft !== null && graceLeft > 0) {
      return { tier: TIERS.PAID, state: 'grace', daysRemaining: graceLeft, reason: 'past_due' };
    }
    return { tier: TIERS.LOCKED, state: 'locked', daysRemaining: 0, reason: 'past_due' };
  }

  // 'trial' (the DB default) and anything unrecognised.
  // A trial only expires when we actually KNOW when it ends. trial_end is absent on most
  // rows today, and locking someone out based on missing data is far worse than letting a
  // stale trial run — so no trial_end means no lock.
  if (billing.trial_end) {
    const left = daysFromNow(billing.trial_end);
    if (left !== null && left <= 0) {
      return { tier: TIERS.LOCKED, state: 'locked', daysRemaining: 0, reason: 'trial_expired' };
    }
    return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: left, reason: null };
  }

  return { tier: TIERS.FREE_TRIAL, state: 'trial', daysRemaining: null, reason: null };
};

/** Convenience wrapper — the tier alone, for the badge and legacy call sites. */
export const getEffectiveTier = (billing) => getBillingState(billing).tier;

/** True when the account is locked out and must pay to continue. */
export const isLockedOut = (tier) => tier === TIERS.LOCKED;

// Copying platform starter templates. Everything is available on trial and paid alike;
// only a locked (unpaid) account is blocked.
export const canCopyTemplates = (userTier) => userTier !== TIERS.LOCKED;
